// porcion.ts — Factores fiables + umbrales de confianza para la presentación del
// análisis (orientación educativa, no clínica).
//
// Espejo de la fuente de verdad del pipeline:
//   services/ai-orchestrator/src/calibration/nutrition-display.ts
// Mantener sincronizado. Base empírica: docs/09_automatizacion_ia_interna/SISTEMA-CALIBRACION.md v2.0.
//
// OJO (2026-08-20): pese a lo de «espejo», esa copia del orquestador NO está
// cableada a nada — la que se ejecuta en producción es ESTA. Los valores de las
// dos son idénticos hoy; cambiar una sin la otra haría que el paciente viera
// una incertidumbre distinta de la calibrada.
//
// Medido contra báscula (41 platos, 2026-07-17):
//  - Identificación/densidad y distribución de macros: FIABLES.
//  - Tamaño de porción autónomo: NO fiable (r²≈0.05) → categoría asistida por el usuario.
import type { ConfidenceLevel } from '@/models/analysis';

/** Umbral de confianza → media-anchura relativa de la banda de kcal (de los MAPE medidos). */
export const KCAL_BAND_HALFWIDTH: Record<ConfidenceLevel, number> = {
  high: 0.2,
  medium: 0.35,
  low: 0.5,
};

export interface PortionBucket {
  id: 'pequena' | 'media' | 'grande' | 'muy_grande';
  label: string;
  minG: number;
  maxG: number;
  typicalG: number;
  /** Referencia visual educativa (mano). */
  referencia: string;
  hand: string;
}

export const PORTION_BUCKETS: readonly PortionBucket[] = [
  { id: 'pequena', label: 'Pequeña', minG: 0, maxG: 120, typicalG: 90, referencia: 'palma ahuecada / puñado', hand: '🤏' },
  { id: 'media', label: 'Media', minG: 120, maxG: 260, typicalG: 190, referencia: 'palma de la mano / 1 puño', hand: '🖐️' },
  { id: 'grande', label: 'Grande', minG: 260, maxG: 400, typicalG: 320, referencia: '2 palmas / 1½ puño', hand: '✋' },
  { id: 'muy_grande', label: 'Muy grande', minG: 400, maxG: 900, typicalG: 500, referencia: '2 puños o más', hand: '👐' },
];

export function bucketForGrams(grams: number): PortionBucket {
  return PORTION_BUCKETS.find((b) => grams >= b.minG && grams < b.maxG) ?? PORTION_BUCKETS[PORTION_BUCKETS.length - 1];
}

export interface KcalBand {
  point: number;
  low: number;
  high: number;
  label: string;
}

/** Estimación puntual + nivel de confianza → rango orientativo de kcal. */
export function kcalBand(point: number, level: ConfidenceLevel): KcalBand {
  const hw = KCAL_BAND_HALFWIDTH[level];
  const r = (n: number) => Math.max(0, Math.round(n / 10) * 10);
  return { point: Math.round(point), low: r(point * (1 - hw)), high: r(point * (1 + hw)), label: `≈${r(point * (1 - hw))}–${r(point * (1 + hw))} kcal` };
}

export interface MacroDistribution {
  carbPct: number;
  protPct: number;
  fatPct: number;
  resumen: string;
}

/** Distribución de macros en % de energía (señal fiable). Suma ≈100. */
export function macroDistribution(carbG: number, protG: number, fatG: number): MacroDistribution {
  const kc = 4 * carbG + 4 * protG + 9 * fatG;
  if (kc <= 0) return { carbPct: 0, protPct: 0, fatPct: 0, resumen: 'sin datos' };
  const carbPct = Math.round((4 * carbG) / kc * 100);
  const protPct = Math.round((4 * protG) / kc * 100);
  const fatPct = Math.max(0, 100 - carbPct - protPct);
  const top = Math.max(carbPct, protPct, fatPct);
  const resumen =
    top < 45 ? 'plato equilibrado'
      : top === carbPct ? 'mayormente hidratos'
        : top === protPct ? 'rica en proteína'
          : 'rica en grasas';
  return { carbPct, protPct, fatPct, resumen };
}
